import { motion } from "framer-motion";
import { Flame, MapPinOff, MailX } from "lucide-react";

const PAINS = [
    {
        icon: Flame,
        stat: "20%",
        statLabel: "of a rep's day",
        title: "Burned CAC",
        body: "Reps burn hours personalizing outreach to people who no longer work there. Every minute spent is dollars set on fire.",
    },
    {
        icon: MapPinOff,
        stat: "1 in 3",
        statLabel: "top accounts",
        title: "Corrupted Territories",
        body: "Your best accounts sit empty because the decision-makers have moved on — and nobody updated the record.",
    },
    {
        icon: MailX,
        stat: "14%+",
        statLabel: "bounce rate",
        title: "Dead Sequences",
        body: "High bounce rates shred your domain reputation, send inbox messages to spam, and silently kill pipeline.",
    },
];

export default function Problem() {
    return (
        <section
            id="problem"
            data-testid="problem-section"
            className="relative py-28 lg:py-36"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="max-w-3xl">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-mono text-[#10B981] font-bold">
                        The Problem
                    </div>
                    <h2 className="mt-4 font-display text-[36px] sm:text-[44px] lg:text-[56px] font-semibold leading-[1.05] tracking-[-0.025em]">
                        The hidden tax on your{" "}
                        <span className="text-volt-gradient">
                            sales velocity.
                        </span>
                    </h2>
                    <p className="mt-5 text-[16px] leading-relaxed text-neutral-400 max-w-2xl">
                        CRM data decays by ~30% per year. The cost doesn't show
                        up on a P&amp;L line — it hides inside your quota, your
                        bounce rate, and your reps' calendars.
                    </p>
                </div>

                <div className="mt-14 grid md:grid-cols-3 gap-5">
                    {PAINS.map((p, i) => (
                        <motion.article
                            key={p.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            data-testid={`problem-card-${i}`}
                            className="group relative rounded-2xl border border-white/5 bg-gradient-to-b from-white/[0.02] to-transparent p-7 hover:border-white/15 transition-colors"
                        >
                            <div className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
                                    <p.icon size={18} strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-display text-4xl font-semibold text-white tracking-tight">
                                        {p.stat}
                                    </div>
                                    <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 mt-0.5">
                                        {p.statLabel}
                                    </div>
                                </div>
                            </div>

                            <h3 className="mt-7 font-display text-[20px] font-medium">
                                {p.title}
                            </h3>
                            <p className="mt-2.5 text-[14px] leading-relaxed text-neutral-400">
                                {p.body}
                            </p>

                            <div className="mt-6 h-px w-full bg-gradient-to-r from-red-500/20 via-white/5 to-transparent" />
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
